import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, MapPin, Phone } from "lucide-react";
import * as Icons from "lucide-react";
import { FOOTER, FINAL_CTA, SOCIALS, INFO_EMAIL, SALES_EMAIL, SUPPORT_EMAIL, BOOKING_URL, ABOUT, CONTACT } from "../mock";
import Logo from "./Logo";

const Footer = () => {
  return (
    <footer className="border-t border-[var(--fr-border)] bg-[var(--fr-bg-alt)]" data-testid="footer">
      <div className="container-x pt-16 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
          <div className="col-span-2">
            <Logo height={26} testid="footer-logo" />
            <p className="mt-4 text-sm text-[var(--fr-ink-2)] max-w-[240px] leading-relaxed">{FOOTER.blurb}</p>
            <a
              href={`mailto:${INFO_EMAIL}`}
              className="mt-4 inline-block text-sm font-semibold text-[var(--fr-primary-dark)] hover:underline"
            >
              {INFO_EMAIL}
            </a>
            <a
              href={FINAL_CTA.whatsapp}
              target="_blank"
              rel="noreferrer"
              className="mt-2 flex items-center gap-2 text-sm text-[var(--fr-ink-2)] hover:text-[var(--fr-ink)]"
            >
              <MessageCircle size={16} className="text-[var(--fr-whatsapp)]" />
              WhatsApp
            </a>
            <div className="mt-5 flex items-center gap-2" data-testid="footer-socials">
              {SOCIALS.map((s) => {
                const Icon = Icons[s.icon] || Icons.Link;
                return (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.name}
                    title={s.name}
                    className="social-btn !w-9 !h-9"
                  >
                    <Icon size={15} />
                  </a>
                );
              })}
            </div>
          </div>

          {FOOTER.columns.map((col) => (
            <div key={col.heading}>
              <h4 className="eyebrow text-[var(--fr-ink)]">{col.heading}</h4>
              <ul className="mt-4 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.href ? (
                      <a
                        href={l.href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`text-sm transition-colors ${
                          l.label === "Log in"
                            ? "font-semibold text-[var(--fr-primary)] hover:text-[var(--fr-primary-dark)]"
                            : "text-[var(--fr-ink-2)] hover:text-[var(--fr-ink)]"
                        }`}
                      >
                        {l.label}
                      </a>
                    ) : (
                      <Link
                        to={l.to}
                        className="text-sm text-[var(--fr-ink-2)] hover:text-[var(--fr-ink)] transition-colors"
                      >
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Head office & contact */}
        <div className="mt-14 pt-10 border-t border-[var(--fr-border)] flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="footer-offices">
          {ABOUT.offices
            .filter((o) => o.entity === "Fleetroot FZCO")
            .map((o) => (
              <div key={o.entity} className="text-sm">
                <div className="flex items-center gap-2">
                  <span className="eyebrow-pill !py-0.5 !px-2 !text-[10px]">{o.country}</span>
                  <span className="font-semibold text-[var(--fr-ink)]">{o.entity}</span>
                </div>
                <p className="mt-3 text-[var(--fr-ink-2)] leading-relaxed flex items-start gap-2">
                  <MapPin size={14} className="mt-[3px] flex-shrink-0 text-[var(--fr-muted)]" />
                  {o.address}
                </p>
              </div>
            ))}
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
            <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 text-[var(--fr-ink-2)] hover:text-[var(--fr-ink)]">
              <Phone size={14} className="text-[var(--fr-muted)]" />
              {CONTACT.phone}
            </a>
            <a href={CONTACT.generalPhoneHref} className="inline-flex items-center gap-2 text-[var(--fr-ink-2)] hover:text-[var(--fr-ink)]">
              <Phone size={14} className="text-[var(--fr-muted)]" />
              {CONTACT.generalPhone}
            </a>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-[var(--fr-border)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[var(--fr-muted)]">
          <span>© {new Date().getFullYear()} Fleetroot. All rights reserved.</span>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            <a href={`mailto:${SALES_EMAIL}`} className="hover:text-[var(--fr-ink)]">
              Sales: {SALES_EMAIL}
            </a>
            <a href={`mailto:${INFO_EMAIL}`} className="hover:text-[var(--fr-ink)]">
              General: {INFO_EMAIL}
            </a>
            <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="footer-support-email" className="hover:text-[var(--fr-ink)]">
              Support: {SUPPORT_EMAIL}
            </a>
            <a href={BOOKING_URL} target="_blank" rel="noreferrer" data-testid="footer-book-call" className="font-semibold text-[var(--fr-primary)] hover:text-[var(--fr-primary-dark)]">
              Book a 30-min call →
            </a>
            <Link to="/legal/privacy-policy" className="hover:text-[var(--fr-ink)]">
              Privacy
            </Link>
            <Link to="/legal/terms-of-service" className="hover:text-[var(--fr-ink)]">
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
